import { LocalDate } from '@js-joda/core';

import { FormContextValues, useFormController } from '@dreryk/react-common';
import { Checkbox, DatePicker, StyledFlex } from '@dreryk/react-components';

import { visitPatientPregnancyStatusTranslationKeys } from '../../translations';

import {
   GynecologicalInterviewPredictedCompletionDateBasisFormBeanPart,
   GynecologicalInterviewPredictedCompletionDateBasisSelect,
} from './GynecologicalInterviewPredictedCompletionDateBasisSelect';

export type PatientPregnancyMarkingFormBean = {
   pregnancyConfirmed: boolean;
   multiplePregnancy: boolean;
   predictedCompletionDate: LocalDate | null;
} & GynecologicalInterviewPredictedCompletionDateBasisFormBeanPart;

type PatientPregnancyMarkingFormChildrenRenderProps = Pick<
   FormContextValues<PatientPregnancyMarkingFormBean>,
   'handleSubmit' | 'formState'
>;

type PatientPregnancyMarkingFormProps = {
   readonly children: (renderProps: PatientPregnancyMarkingFormChildrenRenderProps) => JSX.Element;
};

export const PatientPregnancyMarkingForm = (props: PatientPregnancyMarkingFormProps): JSX.Element => {
   const { register, watch, handleSubmit, formState } = useFormController<PatientPregnancyMarkingFormBean>({
      pregnancyConfirmed: true,
      multiplePregnancy: false,
   });
   const selectedPredictedCompletionDate = watch('predictedCompletionDate');
   return (
      <>
         <StyledFlex direction={'column'} gap={'8px'}>
            <Checkbox
               testId={'pregnancyConfirmed'}
               controller={register('pregnancyConfirmed')}
               label={visitPatientPregnancyStatusTranslationKeys.pregnancyConfirmed}
               // It's semantically important to have it explicit
               // eslint-disable-next-line react/jsx-boolean-value
               defaultValue={true}
            />
            <Checkbox
               testId={'multiplePregnancy'}
               controller={register('multiplePregnancy')}
               label={visitPatientPregnancyStatusTranslationKeys.multiplePregnancy}
            />
         </StyledFlex>
         <StyledFlex gap={'24px'}>
            <DatePicker
               testId={'predictedCompletionDate'}
               controller={register('predictedCompletionDate')}
               label={visitPatientPregnancyStatusTranslationKeys.predictedCompletionDate}
               dayValidators={'dayBeforeToday'}
            />
            {selectedPredictedCompletionDate ? (
               <GynecologicalInterviewPredictedCompletionDateBasisSelect register={register} />
            ) : null}
         </StyledFlex>
         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </>
   );
};
